import type { HTMLAttributes, ReactNode } from 'react';
import { AlertTriangle, Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

/** Shimmer placeholder. Always give it the size of what it stands in for. */
export const Skeleton = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div
    aria-hidden
    className={cn(
      'animate-[shimmer_1.6s_linear_infinite] rounded-sm bg-[linear-gradient(90deg,var(--bg-raised)_25%,var(--bg-hover)_50%,var(--bg-raised)_75%)] bg-[length:200%_100%]',
      className,
    )}
    {...p}
  />
);

/** Region-level loading: announces once, shows skeleton rows. Never a bare spinner (APP-FLOW §9). */
export function SkeletonRows({ rows = 6, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-live="polite" aria-label={label} className="space-y-1.5">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  );
}

interface EmptyProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  /** The next action: import, enrich, clear filters... */
  action?: ReactNode;
  className?: string;
}
export function EmptyState({ title, description, icon: Icon = Inbox, action, className }: EmptyProps) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-4 py-10 text-center', className)}>
      <Icon className="size-6 text-fg-subtle" strokeWidth={1.5} aria-hidden />
      <h3 className="text-md font-semibold">{title}</h3>
      {description && <p className="max-w-[60ch] text-sm text-fg-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Inline error banner with Retry; state what happened and the next action (DESIGN-BRIEF §8). */
export function ErrorState({
  title = "Couldn't load this",
  message,
  onRetry,
  className,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div role="alert" className={cn('flex items-start gap-3 rounded-md border border-dnc/40 bg-dnc-bg p-3', className)}>
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger-fg" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-danger-fg">{title}</p>
        {message && <p className="text-sm text-fg-muted">{message}</p>}
      </div>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}
